import React from 'react';
import { Link } from 'react-router-dom';
import './blocks/ModernBlocks.css';

const ServicesPage = () => {
  const services = [
    {
      id: 'residential-security',
      title: 'Residential Security Systems',
      description: 'Complete home security solutions designed to protect your family and property.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop',
      features: [
        'Smart Home Integration',
        'Mobile App Control',
        'Motion Detection',
        'Door & Window Sensors',
        'Glass Break Detection',
        '24/7 Professional Monitoring',
        'Emergency Response',
        'Video Verification'
      ],
      benefits: [
        'Peace of mind knowing your home is protected',
        'Remote monitoring from anywhere',
        'Instant alerts for any security events',
        'Professional installation and support'
      ]
    },
    {
      id: 'commercial-security',
      title: 'Commercial Security Systems',
      description: 'Advanced security solutions for businesses, offices, and commercial properties.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop',
      features: [
        'Access Control Systems',
        'CCTV Surveillance',
        'Alarm Integration',
        'Visitor Management',
        'Employee Tracking',
        'Remote Management',
        'Audit Trails',
        'Emergency Protocols'
      ],
      benefits: [
        'Protect valuable assets and inventory',
        'Monitor employee access and activity',
        'Deter theft and vandalism',
        'Comply with security regulations'
      ]
    },
    {
      id: 'cctv-installation',
      title: 'CCTV Installation & Monitoring',
      description: 'High-definition surveillance cameras with advanced monitoring capabilities.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'HD & 4K Camera Systems',
        'Night Vision Technology',
        'Remote Viewing',
        'Cloud Storage',
        'Motion Detection',
        'Pan-Tilt-Zoom (PTZ)',
        'Weatherproof Cameras',
        'Mobile App Access'
      ],
      benefits: [
        'Crystal clear video quality day and night',
        'Access footage from anywhere',
        'Secure cloud storage for recordings',
        'Professional installation and maintenance'
      ]
    },
    {
      id: 'alarm-systems',
      title: 'Alarm Systems',
      description: 'Comprehensive alarm systems including burglar, fire, and environmental monitoring.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'Burglar Alarms',
        'Fire Detection Systems',
        'Smoke & Carbon Monoxide',
        'Environmental Sensors',
        'Instant Alerts',
        'Police Dispatch',
        'Fire Department Alert',
        'Medical Emergency Response'
      ],
      benefits: [
        'Early warning for all types of emergencies',
        'Automatic emergency response',
        'Protection against multiple threats',
        'Peace of mind for your family'
      ]
    },
    {
      id: 'access-control',
      title: 'Access Control Systems',
      description: 'Secure entry systems with advanced authentication and visitor management.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'Key Card Systems',
        'Biometric Access',
        'PIN Code Entry',
        'Visitor Management',
        'Audit Trails',
        'Time-based Access',
        'Remote Lock/Unlock',
        'Integration with HR Systems'
      ],
      benefits: [
        'Control who enters your property',
        'Track access history and patterns',
        'Eliminate lost key problems',
        'Enhance overall security'
      ]
    },
    {
      id: 'smart-home-security',
      title: 'Smart Home Security',
      description: 'Intelligent security systems that integrate with your smart home ecosystem.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'Smart Lock Integration',
        'Voice Control',
        'Automated Responses',
        'Energy Management',
        'Lighting Control',
        'Climate Integration',
        'Mobile App Control',
        'AI-Powered Analytics'
      ],
      benefits: [
        'Seamless integration with existing smart devices',
        'Automated security responses',
        'Energy efficiency through smart controls',
        'Enhanced convenience and control'
      ]
    },
    {
      id: 'monitoring-services',
      title: '24/7 Monitoring Services',
      description: 'Professional monitoring center providing round-the-clock security surveillance.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'Live Monitoring',
        'Emergency Response',
        'Police Dispatch',
        'Fire Department Alert',
        'Medical Emergency Response',
        'Video Verification',
        'False Alarm Reduction',
        'Monthly Reports'
      ],
      benefits: [
        'Professional monitoring 24/7/365',
        'Immediate emergency response',
        'Reduced false alarms',
        'Comprehensive incident reporting'
      ]
    },
    {
      id: 'security-consulting',
      title: 'Security Consulting & Risk Assessment',
      description: 'Expert security analysis and customized solutions for your specific needs.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      features: [
        'Security Audits',
        'Risk Assessment',
        'Vulnerability Analysis',
        'Custom Security Plans',
        'Compliance Review',
        'Training Programs',
        'Emergency Planning',
        'Ongoing Support'
      ],
      benefits: [
        'Identify security vulnerabilities',
        'Develop comprehensive security strategies',
        'Ensure compliance with regulations',
        'Train staff on security protocols'
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <div className="services-hero">
        <div className="services-hero-container">
          <h1 className="services-hero-title">Security Solutions</h1>
          <p className="services-hero-subtitle">
            Comprehensive security systems for homes, businesses, and institutions. 
            From residential protection to enterprise-level security, we provide 
            cutting-edge solutions with 24/7 professional monitoring.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="services-main">
        <div className="services-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={service.id} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="service-content">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-features">
                    <h4>Key Features:</h4>
                    <ul>
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="service-benefits">
                    <h4>Benefits:</h4>
                    <ul>
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>

                  <Link to={`/services/${service.id}`} className="service-link">
                    Learn More →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="services-cta">
        <div className="services-cta-container">
          <h2>Ready to Secure Your Property?</h2>
          <p>Get a free security assessment and quote from our experts.</p>
          <div className="services-cta-buttons">
            <Link to="/contact" className="cta-button primary">
              Free Security Assessment
            </Link>
            <a href="tel:+1234567890" className="cta-button secondary">
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage; 